import { Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';
import { environment } from '../../../environments/environment';
import { User, getHighestRole } from '../interfaces/user.interface';

/**
 * Analytics service for Mixpanel event tracking
 * Handles initialization, user identification, and event tracking
 * Respects environment configuration for enabling/disabling tracking
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  /** Flag to track if Mixpanel has been initialized */
  private _initialized = false;

  /**
   * Initialize Mixpanel with environment configuration
   * Should be called once at app startup
   */
  init(): void {
    if (this._initialized || !environment.mixpanel.enabled) {
      return;
    }

    if (!environment.mixpanel.token) {
      console.warn('Mixpanel token not configured');
      return;
    }

    mixpanel.init(environment.mixpanel.token, {
      api_host: environment.mixpanel.apiHost,
      persistence: 'localStorage',
      track_pageview: false, // We'll track page views manually for more control
    });

    this._initialized = true;
  }

  /**
   * Identify the current user for analytics tracking
   * Sets user properties and super properties for all future events
   * @param user - The logged-in user
   */
  identify(user: User): void {
    if (!this._isEnabled()) {
      return;
    }

    // Identify user by Cognito sub
    mixpanel.identify(user.sub);

    // Set super properties (sent with every event)
    mixpanel.register({
      tenant_id: user.tenantId,
      tenant_name: user.tenant,
      user_role: getHighestRole(user.roles),
    });

    // Set user profile properties
    mixpanel.people.set({
      $email: user.email,
      $name: user.name && user.surname ? `${user.name} ${user.surname}` : user.email,
      $first_name: user.name,
      $last_name: user.surname,
      tenant_id: user.tenantId,
      tenant_name: user.tenant,
      roles: user.roles,
      status: user.status,
      lang: user.lang,
      timezone: user.timezone,
    });
  }

  /**
   * Track a custom event
   * @param event - Event name (e.g., 'Button Clicked', 'Page Viewed')
   * @param properties - Optional event properties
   */
  track(event: string, properties?: Record<string, unknown>): void {
    if (!this._isEnabled()) {
      return;
    }

    mixpanel.track(event, properties);
  }

  /**
   * Track a page view event
   * @param pageName - Name of the page
   * @param properties - Optional additional properties
   */
  trackPageView(pageName: string, properties?: Record<string, unknown>): void {
    this.track('Page Viewed', {
      page_name: pageName,
      ...properties,
    });
  }

  /**
   * Reset analytics state (call on logout)
   * Clears user identification and super properties
   */
  reset(): void {
    if (!this._isEnabled()) {
      return;
    }

    mixpanel.reset();
  }

  /**
   * Check if analytics is enabled and initialized
   * @returns True if tracking should occur
   */
  private _isEnabled(): boolean {
    return this._initialized && environment.mixpanel.enabled;
  }
}
